// Placeholder data for the Home screen.
//
// Only the parts of the screen that are still placeholders live here: the
// Planning, Review and Surveillance cards, and the egg-to-adult readout. The
// weather, the wind, the district boundary, the awaiting communications and the
// technician roster come from GET /api/home and are typed in ts/type/api.ts.
// Every block names the Nidus source it stands in for. The figures read like a
// mid-size California mosquito control district: hundreds–low thousands of
// sites, ~6–10 technicians, tens of reports and signals, trap counts in the
// tens–hundreds, VI in the 0–150 band.
import type {
	HomeEggToAdult,
	HomeEggToAdultSpecies,
	HomePlanningCard,
	HomeReviewCard,
	HomeSurveillanceCard,
} from "@/type/api";

// ---------------------------------------------------------------------------
// Workbench cards
// ---------------------------------------------------------------------------

// placeholder — future source: public.signal (created this week) + lead (open)
export const planningCard: HomePlanningCard = {
	new_signals_this_week: 24,
	active_leads: 11,
};

// Review is not developed yet: the panel says so rather than showing a number.
// placeholder — future source: review queue (records created today)
export const reviewCard: HomeReviewCard = {
	new_records_today: null,
};

// placeholder — future source: trap_sample/species_count (pool positivity),
// this week vs the three prior weeks; VI reference line at 80.
export const surveillanceCard: HomeSurveillanceCard = {
	trap_counts: {
		this_week: 214,
		prior_weeks: [186, 172, 163],
		prior_week_labels: ["Oct 7", "Sep 30", "Sep 23"],
		reference: null,
	},
	vector_index: {
		this_week: 62,
		prior_weeks: [48, 55, 41],
		prior_week_labels: ["Oct 7", "Sep 30", "Sep 23"],
		reference: 80,
	},
	trend_basis: "this week vs the three prior weeks",
};

// ---------------------------------------------------------------------------
// Egg to adult
// ---------------------------------------------------------------------------

// placeholder — deliberately so. The figure divides each species' degree-day
// requirement by the current daily rate, but a meaningful rate needs a season
// of daily temperature history at the district, which the weather log does not
// carry yet (a separate schema change, pending sign-off). Until it lands this
// block shows the mid-season figures below rather than a live reading.
//
// Days from egg to adult divide each species' degree-day requirement by the
// rolling 7-day mean daily rate at base 10 °C, average method, biofix Jan 1.
const EGG_TO_ADULT_DAILY_DD_RATE = 12.8;

const eggToAdultRequirements = [
	{ species: "Ae. aegypti", degree_days: 105.5 },
	{ species: "Cx. quinquefasciatus", degree_days: 137.0 },
	{ species: "Cx. tarsalis", degree_days: 145.4 },
];

const eggToAdultSpecies: HomeEggToAdultSpecies[] = eggToAdultRequirements.map(
	(s) => ({ ...s, days: s.degree_days / EGG_TO_ADULT_DAILY_DD_RATE }),
);

export const eggToAdult: HomeEggToAdult = {
	daily_degree_day_rate: EGG_TO_ADULT_DAILY_DD_RATE,
	average_days:
		eggToAdultSpecies.reduce((sum, s) => sum + s.days, 0) /
		eggToAdultSpecies.length,
	species: eggToAdultSpecies,
};
